import { useEffect, useRef, useState } from 'react';
import { FormInputProps } from '@/@types/form.types';
import EListPopupPickerWithSearch from '@/components/everridge/EListPopupPickerWithSearch';
import { useOrderAuthorsLookupApi } from '@/features/orders/hooks/useOrderAuthorsLookupApi';

type Props<T extends {}> = {
	selectVariant?: 'single' | 'multi';
	showSearch?: boolean;
	icon?: React.FC<React.SVGProps<SVGSVGElement>>;
} & FormInputProps<T>;

const OrderAuthorsLookup = <T extends {}>(props: Props<T>) => {
	const [searchValue, setSearchValue] = useState('');
	const { placeholder = 'Select author', label = 'Ordered by' } = props;

	const loaderRef = useRef<HTMLDivElement>(null);

	const {
		orderAuthorsLookupList, 
		fetchOrderAuthorsLookupListNextPage,
		hasOrderAuthorsLookupListNextPage,
		isFetchingOrderAuthorsLookupListNextPage,
	} = useOrderAuthorsLookupApi({ search: searchValue });

	useEffect(() => {
		if (
			!loaderRef.current ||
			!hasOrderAuthorsLookupListNextPage ||
			isFetchingOrderAuthorsLookupListNextPage
		)
			return;

		const scrollableContainer =
			loaderRef.current.closest('.overflow-y-scroll');

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					fetchOrderAuthorsLookupListNextPage();
				}
			},
			{
				root: scrollableContainer,
				rootMargin: '100px',
			},
		);

		const current = loaderRef.current;
		observer.observe(current);

		return () => observer.unobserve(current);
	}, [
		fetchOrderAuthorsLookupListNextPage,
		hasOrderAuthorsLookupListNextPage,
		isFetchingOrderAuthorsLookupListNextPage,
	]);

	return (
		<EListPopupPickerWithSearch
			{...props}
			label={label}
			placeholder={placeholder}
			list={orderAuthorsLookupList ?? []}
			searchValue={searchValue}
			setSearchValue={setSearchValue}
			ref={loaderRef}
		/>
	);
};

export default OrderAuthorsLookup;
